import { Box, Divider, Typography, useMediaQuery } from '@mui/material';
import {
  IStyles,
  IconWrapper,
  formatDateAsDayMonth,
  getDifficultyLevelString,
  pxToRem,
  pxTovW,
  theme,
} from '@geneo2-web/shared-ui';
import { DifficultyLevelEnum } from '@protos/content_management/content.db_pb';
import { Timestamp } from '@bufbuild/protobuf';
import { useGlobalContext } from '../../app/Context/GlobalContextProvider';

const styles: IStyles = {
  overviewBox: {
    width: { xs: '100%', md: '100%', lg: '100%' },
    boxSizing: 'border-box',
    borderRadius: { xs: '0px', md: pxToRem(10) },
    backgroundColor: '#FFFFFF',
    border: { md: '1px solid #E0DFDE' },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: { xs: pxToRem(20), md: pxTovW(35) },
    boxShadow: `0px 0px ${pxToRem(10)} #0000001F`,
    paddingX: { xs: pxToRem(10), md: pxTovW(25) },
    paddingY: { xs: pxToRem(10), md: pxTovW(15) },
    paddingBottom: { xs: pxToRem(10), md: pxTovW(35) },
  },
  overviewHeading: {
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    gap: pxToRem(20),
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  viewAssessmentButton: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(4), md: pxTovW(8) },
    cursor: 'pointer',
    borderRadius: { xs: pxToRem(5), md: pxTovW(15) },
    backgroundColor: '#158D29',
    padding: { xs: pxToRem(8), md: pxTovW(10) },
  },
  overviewDetailsBox: {
    width: '100%',
    display: 'flex',
    justifyContent: { xs: 'space-around', md: 'space-between' },
    boxSizing: 'border-box',
    alignItems: 'center',
  },

  // ------------ DataLabelBox ------------
  dataLabelBox: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { xs: 'center', md: 'flex-start' },
    gap: { xs: pxToRem(15), md: pxTovW(15) },
    justifyContent: 'left',
  },

  dataAndLabel: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'left',
    gap: { xs: pxToRem(5.5), md: pxTovW(7.5) },
  },
};

interface IProps {
  numberOfQuestions?: number;
  difficultyLevel?: DifficultyLevelEnum;
  assessmentTargetDate?: Timestamp;
  viewAssessmentCTA: () => void;
}
export const OverviewBox = (props: IProps) => {
  const {
    numberOfQuestions,
    difficultyLevel,
    assessmentTargetDate,
    viewAssessmentCTA,
  } = props;

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isIpadOnly } = useGlobalContext();

  return (
    <Box sx={styles.overviewBox}>
      <Box sx={styles.overviewHeading}>
        <Typography variant="bodyText" fontWeight="bold">
          Test Overview
        </Typography>
        <Box onClick={viewAssessmentCTA} sx={styles.viewAssessmentButton}>
          <IconWrapper
            name="reports"
            size={isIpadOnly ? 'large' : 'md'}
            parentFolder="icons"
            type="png"
          />
          <Typography
            variant="smallText"
            fontWeight="bold"
            sx={{ color: '#FFFFFF' }}
          >
            View Assessment
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.overviewDetailsBox}>
        <DataLabelBox
          iconName="questions"
          data={isMobile ? `${numberOfQuestions} Qs` : `${numberOfQuestions}`}
          label={isMobile ? undefined : 'Questions'}
          showDivider
        />

        <DataLabelBox
          iconName="bar-graph"
          data={getDifficultyLevelString(difficultyLevel)}
          label={isMobile ? undefined : 'Difficulty'}
          showDivider
        />

        <DataLabelBox
          iconName="calender"
          data={formatDateAsDayMonth(assessmentTargetDate)}
          label={isMobile ? undefined : 'Deadline'}
        />
      </Box>
    </Box>
  );
};

interface IDataLabelBox {
  iconName: string;
  data: string;
  label?: string;
  showDivider?: boolean;
}
const DataLabelBox = (props: IDataLabelBox) => {
  const { iconName, data, label, showDivider } = props;

  return (
    <>
      <Box sx={styles.dataLabelBox}>
        <IconWrapper
          name={iconName}
          size="large"
          parentFolder="icons"
          type="png"
          customSx={{
            height: { xs: pxToRem(25), lg: pxTovW(24.43) },
            width: { xs: pxToRem(25), lg: pxTovW(26.43) },
            mt: pxToRem(4),
          }}
        />
        <Box sx={styles.dataAndLabel}>
          <Typography variant="h2" fontWeight="bold">
            {data}
          </Typography>
          {label && (
            <Typography
              variant="h4"
              fontWeight="regular"
              sx={{ display: { xs: 'none', md: 'unset' } }}
            >
              {label}
            </Typography>
          )}
        </Box>
      </Box>

      {showDivider ? (
        <Divider orientation="vertical" variant="middle" flexItem />
      ) : null}
    </>
  );
};
